import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Draw, DrawWinner } from '../../types';
import { Icon } from '../../components/participant/Icon';
import { ParticipantTopBar } from '../../components/participant/ParticipantTopBar';
import { participantColors as c, fadeUpKeyframes } from '../../theme/participantTheme';

interface WinnersParticipantProps {
  draw: Draw | null;
  winners: DrawWinner[];
  loading: boolean;
}

const PLACE_LABELS = ['1º Lugar', '2º Lugar', '3º Lugar'];

/**
 * Ganhadores — visão do participante (role USUARIO_FINAL): só ganhadores
 * aprovados, sem os controles de aprovar/rejeitar (esses são só do admin).
 * Renderizado por DrawWinners.tsx quando user.role === 'USUARIO_FINAL'.
 */
export const WinnersParticipant: React.FC<WinnersParticipantProps> = ({ draw, winners, loading }) => {
  const navigate = useNavigate();
  // API devolve do mais recente ao mais antigo; no podio o 1º lugar é o
  // primeiro sorteado (mais antigo).
  const rankedWinners = [...winners].reverse();

  return (
    <Box sx={{ minHeight: '100vh', background: c.gradients.pageBg, fontFamily: c.fontFamily, pb: '48px' }}>
      <style>{fadeUpKeyframes}</style>

      <Box sx={{ maxWidth: 620, mx: 'auto', px: 2, display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <ParticipantTopBar />

        {/* Hero */}
        <Box
          sx={{
            background: c.surfaceDark,
            color: c.onDarkPrimary,
            borderRadius: `${c.radius.cardLarge}px`,
            padding: '26px 22px',
            boxShadow: c.shadows.darkHeroLarge,
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box
              component="button"
              onClick={() => navigate('/draws')}
              title="Voltar"
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                border: 'none',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.12)',
                color: c.onDarkPrimary,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="arrow_back" size={22} />
            </Box>
            <Box component="div" sx={{ fontSize: 21, fontWeight: 800 }}>
              {draw?.campaign?.name || 'Sorteio'}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(245,176,27,0.18)',
                color: c.amber,
                borderRadius: `${c.radius.pill}px`,
                padding: '6px 13px',
                fontSize: 13.5,
                fontWeight: 800,
              }}
            >
              <Icon name="emoji_events" size={16} />
              {draw?.prize?.name || 'Prêmio'}
            </Box>
            {draw?.drawDate && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'rgba(255,255,255,0.12)',
                  color: c.onDarkSecondary,
                  borderRadius: `${c.radius.pill}px`,
                  padding: '6px 13px',
                  fontSize: 13.5,
                  fontWeight: 700,
                }}
              >
                <Icon name="calendar_month" size={16} />
                {new Date(draw.drawDate).toLocaleDateString('pt-BR')}
              </Box>
            )}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: c.brandMid }} size={48} />
          </Box>
        ) : rankedWinners.length === 0 ? (
          <Box sx={{ background: c.surface, borderRadius: `${c.radius.card}px`, padding: '40px 24px', textAlign: 'center', boxShadow: c.shadows.card }}>
            <Icon name="emoji_events" size={48} color={c.iconMuted} />
            <Box component="div" sx={{ fontSize: 17, fontWeight: 700, color: c.textSecondary, mt: '12px' }}>
              {draw?.status === 'IN_PROGRESS'
                ? 'Nenhum ganhador aprovado ainda. Assim que a associação aprovar, aparece aqui.'
                : 'Ganhadores ainda não divulgados'}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {rankedWinners.map((winner, i) => {
              const isTop = i === 0;
              return (
                <Box
                  key={winner.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    background: c.surface,
                    border: `2px solid ${isTop ? c.amber : 'transparent'}`,
                    borderRadius: `${c.radius.card}px`,
                    padding: '18px 20px',
                    boxShadow: c.shadows.listItem,
                    animation: `participantFadeUp ${0.3 + i * 0.05}s ease both`,
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      flexShrink: 0,
                      borderRadius: '50%',
                      background: isTop ? c.amber : c.iconMuted,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name="emoji_events" size={28} color={c.onDarkPrimary} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      component="div"
                      sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase', color: isTop ? c.amberTextDark : c.textSecondary }}
                    >
                      {PLACE_LABELS[i] || `${i + 1}º Lugar`}
                    </Box>
                    <Box component="div" sx={{ fontSize: 18.5, fontWeight: 800, color: c.textDarkest, mt: '2px' }}>
                      {winner.user?.name || 'Ganhador'}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '4px', flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Icon name="confirmation_number" size={14} color={c.textTertiary} />
                        <Box component="span" sx={{ fontSize: 13.5, color: c.textTertiary }}>
                          #{winner.winningCoupon?.number || 'N/A'}
                        </Box>
                      </Box>
                      {winner.winningCoupon?.store?.name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Icon name="storefront" size={14} color={c.textTertiary} />
                          <Box component="span" sx={{ fontSize: 13.5, color: c.textTertiary }}>
                            {winner.winningCoupon.store.name}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: c.amberBg,
            border: `2px solid ${c.amber}`,
            borderRadius: `${c.radius.cardLarge}px`,
            padding: '16px 18px',
          }}
        >
          <Icon name="stars" size={30} color={c.amber} />
          <Box component="span" sx={{ fontSize: 15.5, fontWeight: 700, color: c.amberTextDark }}>
            Continue cadastrando cupons para concorrer aos próximos sorteios!
          </Box>
        </Box>

        <Box
          component="button"
          onClick={() => navigate('/user/my-coupons/new')}
          sx={{
            width: '100%',
            minHeight: 58,
            border: 'none',
            borderRadius: `${c.radius.button}px`,
            background: c.gradients.primaryButton,
            color: c.onDarkPrimary,
            fontFamily: c.fontFamily,
            fontSize: 17,
            fontWeight: 800,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: c.shadows.primaryButton,
            transition: 'transform 0.15s',
            '&:hover': { transform: 'translateY(-2px)' },
          }}
        >
          <Icon name="add_circle" size={22} />
          Cadastrar mais cupons
        </Box>
      </Box>
    </Box>
  );
};
